import { useMemo } from 'react';
import type { ArcData, SectionStatus } from '../../types/canvas';
import { ActionBar } from './ActionBar';

interface VisualCardProps {
  data: ArcData;
  status: SectionStatus;
  onApprove?: () => void;
  onRefine?: () => void;
  onAnother?: () => void;
  refineDisabled?: boolean;
}

function buildCurvePath(moments: ArcData['keyMoments'], width: number, height: number): string {
  if (moments.length < 2) return '';
  const padding = 40;
  const w = width - padding * 2;
  const h = height - padding * 2;

  const points = moments.map((m) => ({
    x: padding + m.position * w,
    y: padding + h - (m.position < 0.5
      ? m.position * 2 * h * 0.6
      : (0.6 + (m.position - 0.5) * 2 * 0.4) * h),
  }));

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
  }
  return d;
}

export function VisualCard({ data, status, onApprove, onRefine, onAnother, refineDisabled }: VisualCardProps) {
  const svgWidth = 600;
  const svgHeight = 180;
  const padding = 40;

  const path = useMemo(
    () => buildCurvePath(data.keyMoments, svgWidth, svgHeight),
    [data.keyMoments],
  );

  const dotPositions = useMemo(() => {
    const w = svgWidth - padding * 2;
    const h = svgHeight - padding * 2;
    return data.keyMoments.map((m) => ({
      x: padding + m.position * w,
      y: padding + h - (m.position < 0.5
        ? m.position * 2 * h * 0.6
        : (0.6 + (m.position - 0.5) * 2 * 0.4) * h),
      label: m.label,
      character: m.character,
    }));
  }, [data.keyMoments]);

  return (
    <div className="col-span-full bg-surface border border-border rounded-lg p-6 sm:px-7">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-display italic text-lg text-foreground [font-variation-settings:'opsz'_96,'SOFT'_80]">
          Tension Arc
        </h3>
      </div>

      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="w-full h-[180px] block"
        aria-label="Tension arc visualization"
      >
        <path d={path} fill="none" stroke="#C2410C" strokeWidth="2" opacity="0.7" />
        {dotPositions.map((pt, i) => (
          <g key={i}>
            <circle cx={pt.x} cy={pt.y} r="5" fill="#FAFAF7" stroke="#C2410C" strokeWidth="1.5" />
            <text
              x={pt.x}
              y={pt.y - 12}
              textAnchor="middle"
              className="fill-foreground text-[9px]"
              style={{ fontFamily: 'General Sans, sans-serif' }}
            >
              {pt.label.length > 25 ? pt.label.slice(0, 22) + '...' : pt.label}
            </text>
          </g>
        ))}
      </svg>

      {data.description && (
        <p className="font-display text-sm italic text-muted-foreground mt-3 [font-variation-settings:'opsz'_48,'SOFT'_80]">
          {data.description}
        </p>
      )}

      <ActionBar status={status} onApprove={onApprove} onRefine={onRefine} onAnother={onAnother} refineDisabled={refineDisabled} />
    </div>
  );
}
